import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import BlockContent from '@sanity/block-content-to-react';

import colors from '@utils/colors';
import Container from '@components/Container';

import * as styled from './styles';

const COLOR_PAIRS = {
  blue: { dark: colors.B500, light: colors.B200 },
  green: { dark: colors.G500, light: colors.G200 },
  orange: { dark: colors.O500, light: colors.O200 },
  purple: { dark: colors.P500, light: colors.P200 },
};

function Band({ label, content, background, imageSide }) {
  const legacyItems = Array.isArray(content) && typeof content[0] === 'string';

  return (
    <styled.Band $background={background}>
      <styled.VerticalLabel>{label}</styled.VerticalLabel>
      <styled.Content $imageSide={imageSide}>
        {legacyItems ? (
          <ul>
            {content.map((item, index) => <li key={index}>{item}</li>)}
          </ul>
        ) : (
          <BlockContent blocks={content || []} />
        )}
      </styled.Content>
    </styled.Band>
  );
}

Band.propTypes = {
  label: PropTypes.string.isRequired,
  content: PropTypes.array,
  background: PropTypes.string.isRequired,
  imageSide: PropTypes.oneOf(['left', 'right']).isRequired,
};

Band.defaultProps = {
  content: [],
};

function Photo({ image, role }) {
  const fluid = get(image, 'asset.fluid');

  if (!fluid) {
    return null;
  }

  return (
    <styled.Photo data-role={role}>
      <styled.PhotoImage
        fluid={fluid}
        alt={image.altText || ''}
        loading="lazy"
      />
    </styled.Photo>
  );
}

Photo.propTypes = {
  image: PropTypes.shape({
    asset: PropTypes.shape({ fluid: PropTypes.object }),
    altText: PropTypes.string,
  }),
  role: PropTypes.oneOf(['actions', 'outcomes']).isRequired,
};

Photo.defaultProps = {
  image: null,
};

function HorizonsChart({ actionsLabel, description, grades, heading, outcomesLabel }) {
  if (!grades.length) {
    return null;
  }

  return (
    <styled.Wrapper>
      <styled.Layout>
        <Container centered>
          {(heading || description) && (
            <styled.Intro>
              {heading && <styled.SectionHeading level={200}>{heading}</styled.SectionHeading>}
              {description && <styled.Description>{description}</styled.Description>}
            </styled.Intro>
          )}

          <styled.Chart>
            {grades.map((grade, index) => {
            const color = COLOR_PAIRS[grade.color || grade.hue] || COLOR_PAIRS.blue;
            const imageSize = grade.actionsImageSize || 'large';
            const imageSide = grade.actionsImageSide || 'right';
            const imageLayer = grade.actionsImageLayer || 'front';

            return (
              <styled.Grade key={grade._key || index}>
                <styled.GradeHeading level={300}>
                  {grade.heading}
                </styled.GradeHeading>

                <styled.GradeBody>
                  <Band
                    label={actionsLabel}
                    content={grade.actions}
                    background={color.light}
                    imageSide={imageSide}
                  />
                  <Band
                    label={outcomesLabel}
                    content={grade.outcomes}
                    background={color.dark}
                    imageSide={imageSide}
                  />

                  <styled.Photos
                    $imageSize={imageSize}
                    $imageSide={imageSide}
                    $imageLayer={imageLayer}
                  >
                    <Photo image={grade.actionsImage} role="actions" />
                    <Photo image={grade.outcomesImage} role="outcomes" />
                  </styled.Photos>
                </styled.GradeBody>
              </styled.Grade>
            );
            })}
          </styled.Chart>
        </Container>
      </styled.Layout>
    </styled.Wrapper>
  );
}

HorizonsChart.propTypes = {
  actionsLabel: PropTypes.string,
  heading: PropTypes.string,
  description: PropTypes.string,
  outcomesLabel: PropTypes.string,
  grades: PropTypes.arrayOf(PropTypes.shape({
    _key: PropTypes.string,
    heading: PropTypes.string,
    color: PropTypes.oneOf(['blue', 'green', 'orange', 'purple']),
    hue: PropTypes.oneOf(['blue', 'green', 'orange', 'purple']),
    actionsImageSize: PropTypes.oneOf(['large', 'small']),
    actionsImageSide: PropTypes.oneOf(['left', 'right']),
    actionsImageLayer: PropTypes.oneOf(['front', 'behind']),
    actions: PropTypes.array,
    outcomes: PropTypes.array,
    actionsImage: PropTypes.object,
    outcomesImage: PropTypes.object,
  })),
};

HorizonsChart.defaultProps = {
  actionsLabel: 'Actions',
  heading: null,
  description: null,
  outcomesLabel: 'Outcomes',
  grades: [],
};

export default HorizonsChart;
